import { useState } from "react";
import type { AnalyticsScope } from "../queryKeys";
import type {
  ContextResponse,
  DateRange,
  MetricName,
  Overview,
  Timeseries,
} from "../types";
import { useWebsiteReport } from "../useAnalyticsContext";
import MetricGrid from "../components/MetricGrid";
import TrendChart from "../components/TrendChart";
import InsightsPanel from "../components/InsightsPanel";
import PageChanges from "../components/PageChanges";
import { ErrorState, Loading, MissingSource } from "../components/States";
import styles from "../Analytics.module.css";
export interface ViewProps {
  scope: AnalyticsScope;
  id: string;
  range: DateRange;
  context: ContextResponse;
}
export default function OverviewView({ scope, id, range, context }: ViewProps) {
  const [metric, setMetric] = useState<MetricName>(
    context.sources.gsc.connection_status !== "not_connected"
      ? "clicks"
      : "sessions",
  );
  const overview = useWebsiteReport<Overview>(
    scope,
    id,
    range,
    context,
    "overview",
  );
  const source = ["clicks", "impressions", "ctr", "position"].includes(metric)
    ? "gsc"
    : "ga4";
  const trend = useWebsiteReport<Timeseries>(
    scope,
    id,
    range,
    context,
    "timeseries",
    { source, metric },
    context.sources[source].connection_status !== "not_connected",
  );
  const links = `/analytics/websites/${id}/connections?start=${range.start}&end=${range.end}&compare=${range.compare}`;
  return (
    <div className={styles.stack}>
      {overview.isPending ? (
        <Loading />
      ) : overview.isError ? (
        <ErrorState error={overview.error} retry={() => overview.refetch()} />
      ) : (
        <MetricGrid
          metrics={overview.data.payload.metrics}
          names={["clicks", "impressions", "sessions", "users"]}
          compare={range.compare === "previous"}
          selected={metric}
          onSelect={setMetric}
        />
      )}
      <div className={styles.sourcePrompts}>
        {(["gsc", "ga4"] as const).map(
          (s) =>
            context.sources[s].connection_status === "not_connected" && (
              <MissingSource key={s} source={s} href={links} />
            ),
        )}
      </div>
      {context.sources[source].connection_status !== "not_connected" &&
        (trend.isPending ? (
          <Loading label="Loading trend…" />
        ) : trend.isError ? (
          <ErrorState error={trend.error} retry={() => trend.refetch()} />
        ) : (
          <TrendChart
            data={trend.data.payload}
            compare={range.compare === "previous"}
          />
        ))}
      {!!overview.data?.payload.highlights?.length && (
        <section className={styles.panel}>
          <div className={styles.panelHeading}>
            <div>
              <h2>What changed</h2>
              <p>Observed in the selected reports</p>
            </div>
          </div>
          <div className={styles.highlights}>
            {overview.data?.payload.highlights.map((item, i) => (
              <article key={i}>
                <small>{item.source}</small>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </article>
            ))}
          </div>
        </section>
      )}
      {(context.sources.gsc.connection_status !== "not_connected" ||
        context.sources.ga4.connection_status !== "not_connected") && (
        <PageChanges scope={scope} id={id} range={range} context={context} />
      )}
      <InsightsPanel scope={scope} id={id} range={range} context={context} />
    </div>
  );
}
